// Actions are all about taking an app situation (some sort of pseudo event like 'bootingStarted' or 'mapClicked') as an action and committing to the app's state.
// Ideally actions should be short and sweet and we should avoid as much logic as possible.
// Avoid dispatching other actions from within actions (nesting actions).
// More complex actions should be moved into a separate handler file.
// Shared behaviour can be abstracted into a helper function.

import { openPaneComponent } from "./actions/helpers/pane_helpers";
import {
  getCurrentPaneOptionComponent,
  selectPaneOption,
} from "./actions/helpers/pane_option_helpers";
import { initFromPersistentStore } from "../../../../lib/mixins/persistable";
import { getLastPaneOptionFromOpenedMapFilterCodes } from "./actions/helpers/map_filter_helpers";
// setBooting comes from the nav's OWN commit helpers — importing it from the online-app SM's
// commit_helpers (as before) needlessly coupled the nav bundle to online-app code.
import { recordPaneOptionsComponentOpenState, setBooting, setNavOpen as commitNavOpen } from "./actions/helpers/commit_helpers";

export default {
  bootingStarted(sm) {
    setBooting(sm, true);
  },

  bootingEnded(sm) {
    setBooting(sm, false);
  },

  initFromPersistentStore,

  // The Nav webview owns nav open/closed state (plan 2026-07-15). Nav.openNav/closeNav/toggleNav
  // dispatch this to persist the change; the commit auto-persists (navOpen is in persist.js).
  setNavOpen(sm, open) {
    commitNavOpen(sm, open);
  },

  openPane(sm, pane) {
    openPaneComponent(sm, pane);
  },

  // This action is called when the URL changes with a route request. This normally happens when:
  // - a user navigates to a URL directly or uses the back/forward commands (all handled by the OnlineApp JS).
  // These will dispatch a `OnlineAppNavOptionConfig.selectedWithRouteRequestEventName` event which is picked up by the `PaneOptionComponent`.
  // The `PaneOptionComponent` then calls this action.
  selectPaneOptionComponentWithRouteRequest(sm, payload) {
    const paneOptionComponent = payload.paneOptionComponent;
    // The INITIAL deep-link route resolution runs right after boot and would otherwise re-apply
    // the pane option's nav-toggle instruction, clobbering the just-restored persisted navOpen
    // (the boot-time `_suppressNavInstruction` guard has already reset by then). Suppress the
    // instruction for that first resolution only — subsequent (back/forward) route requests keep
    // applying it. WEB-ONLY in effect: native has no OnlineAppRouter, so this path never fires there.
    const select = () => this.paneOptionSelect(sm, paneOptionComponent, { suppressNavInstruction: !!payload.isInitialRoute });

    // A route request is the person's action (a deep link, back/forward), so its selection counts.
    // But a later one that resolves to the option ALREADY selected (a map marker tap, Back out of a
    // submarker) changes nothing in the nav, so it isn't counted. The initial route always counts:
    // it is the deep link the person opened, even when boot already restored that same option.
    // See plans/2026-09-24--nav-programmatic-selections-counted.md.
    const reselection = !payload.isInitialRoute && paneOptionComponent.isSelected && paneOptionComponent.isSelected();
    const nav = sm.getStateableObject();
    if (reselection) {
      withUntrackedSelection(nav, select);
    } else if (payload.isInitialRoute && nav && nav.withPaneOpenBaseline) {
      // Judge the deep link's pane open against the pane the nav booted on, not the one the boot
      // restore switched to: the person never saw that one.
      nav.withPaneOpenBaseline(nav.bootPaneId, select);
    } else {
      select();
    }
  },

  viewPaneOptionComponent(sm, paneOptionComponent) {
    paneOptionComponent.trackViewed();
  },

  /**
   * This action might be dispatched when a user clicks on a pane option sub-component or when the URL changes with a route request.
   *
   * @param sm
   * @param paneOptionComponent
   */
  paneOptionSelect(sm, paneOptionComponent, options = {}) {
    const nav = sm.getStateableObject(); // Get a reference to the `Nav` object via the state machine.

    // A pane option can carry a nav-toggle instruction (open/close the nav when selected).
    // Nav.openNav/closeNav now persist the change, so this instruction is a real state write.
    // During boot we suppress it when a persisted navOpen exists — the remembered state wins
    // over the initial pane option's instruction (plan 2026-07-15, "persisted state wins").
    // `options.suppressNavInstruction` extends that to the initial deep-link route resolution
    // (plan 2026-07-20 follow-up); it defaults to false, so direct selections — a user tap
    // (`paneLinkClick`, the native drawer's path too) and in-session route changes — are unchanged.
    const suppressNavInstruction = nav && (nav._suppressNavInstruction || options.suppressNavInstruction);
    if (nav && !suppressNavInstruction) {
      const navToggleInstruction =
        paneOptionComponent.getNavToggleInstruction();
      if (navToggleInstruction === "open") nav.openNav();
      if (navToggleInstruction === "close") nav.closeNav();
    }

    selectPaneOption(sm, paneOptionComponent);

    if (paneOptionComponent.shouldOpenPane()) {
      // TODO - Eventually we can remove this deprecated recording of showing content. This is only for older native apps up until Oct 2024.
      // Communicate only with older native apps via `data-nav_is_showing_content` data attribute as to whether the pane option is showing an associated content pane.
      // The native apps decide whether to keep the nav open or not based on this attribute. However it's not a great solution as sometimes the nav should be open even if the pane is not showing content.
      paneOptionComponent.recordShowingContent();

      openPaneComponent(sm, paneOptionComponent.getAssociatedPane());
    } else {
      // If the nav pane is not showing content then we want to close the pane.
      if (!paneOptionComponent.isNavPaneShowingContent()) {
        // We should close any existing pane (eg. open the primary pane). Otherwise we can end up with a map showing content unrelated to the open pane.
        const primaryPane = nav?.panesManager?.defaultPane;
        if (primaryPane) openPaneComponent(sm, primaryPane);
      }
    }
  },

  /**
   * Pane Link Click Event Process:
   * For clicks on the main link of a pane option we call the `paneOptionSelect` action to do all the standard work.
   *
   * @param sm
   * @param paneLinkComponent
   */
  paneLinkClick(sm, paneLinkComponent) {
    const paneOptionComponent = paneLinkComponent.paneOptionComponent;
    if (!paneOptionComponent) return;

    this.paneOptionSelect(sm, paneOptionComponent);
  },

  /**
   * Pane Info Click Event Process:
   * For clicks on the info icon of a pane option we want to always show the info pane, so we set the `willShowPaneOverride` to `true`.
   * With that configuration done we can simply we call the `paneLinkClick` action to do all the standard work.
   *
   * @param sm
   * @param paneInfoComponent
   */
  paneInfoClick(sm, paneInfoComponent) {
    const paneOptionComponent = paneInfoComponent.paneOptionComponent;
    if (!paneOptionComponent) return;

    paneOptionComponent.willShowPaneOverride = true; // Because this is an 'info' click we will always show the pane.
    this.paneOptionSelect(sm, paneOptionComponent);
    paneOptionComponent.willShowPaneOverride = null; // Reset the flag.
  },

  closePaneClick(sm, closePaneComponent) {
    openPaneComponent(
      sm,
      closePaneComponent.pane.getPanesManager().defaultPane,
    ); // Open the primary pane (which closes the current one).

    // If the current pane option (the one that opened the pane we're closing) has no map filter then we're going to be left with a blank map without markers (bummer!).
    // So let's automatically "click" the most recent pane option that does have a map filter.
    // Because it's a "click" (rather than just changing state) the `Nav` and the `OnlineApp` (and native apps) will all respond appropriately.
    // Clicking the most recent pane option that has a map filter will therefore mean that it is also the "next" pane option.
    const currentPaneOptionComponent = getCurrentPaneOptionComponent(sm);
    if (!currentPaneOptionComponent.hasMapFilter) {
      const nextPaneOptionComponent =
        getLastPaneOptionFromOpenedMapFilterCodes(sm);
      if (nextPaneOptionComponent && nextPaneOptionComponent.hasMapFilter) {
        // We configure the next pane option to behave as we want it to - to open the nav and not show its associated pane.
        // Note that this config could be sent as a detail payload in the event that the next pane option listens for.
        // However we use the `click()` function to fire the event (rather than a custom event) so we can't send a payload.
        nextPaneOptionComponent.willShowPaneOverride = false; // We're using this to force the next pane option to *not* show its associated pane regardless of its standard behaviour.
        nextPaneOptionComponent.navToggleInstructionOverride = "open"; // We're using this to force the next pane option to *open* the nav regardless of its standard behaviour.
        // The person tapped close, not this option, so the reselection isn't counted as a selection.
        withUntrackedSelection(sm.getStateableObject(), () => {
          nextPaneOptionComponent.fireClickEventOnLink({
            willShowPaneOverride: false,
            navToggleInstructionOverride: "open",
          }); // Fire the click event on the next pane option and pass the config as a payload.
        });
        nextPaneOptionComponent.navToggleInstructionOverride = null; // Reset the the flag.
        nextPaneOptionComponent.willShowPaneOverride = null; // Reset the the flag to ensure the next pane option behaves as normal.
      }
    }
  },

  paneOptionsComponentOpen(sm, paneOptionsComponent) {
    recordPaneOptionsComponentOpenState(sm, {
      paneOptionsComponent: paneOptionsComponent,
      open: true,
    });
  },

  paneOptionsComponentClose(sm, paneOptionsComponent) {
    recordPaneOptionsComponentOpenState(sm, {
      paneOptionsComponent: paneOptionsComponent,
      open: false,
    });
  },
};

// Run `fn` as a selection nobody chose, via the `Nav` (see `Nav#withUntrackedSelection`). Without a
// Nav to hold the flag there's nothing to skip, so `fn` just runs.
function withUntrackedSelection(nav, fn) {
  if (nav && nav.withUntrackedSelection) return nav.withUntrackedSelection(fn);
  return fn();
}
